import { BellOutlined, FolderOutlined, MenuFoldOutlined, MenuUnfoldOutlined, ScheduleOutlined } from '@ant-design/icons';
import { Avatar, Badge, Col, Dropdown, Layout, Modal, Row, Tag, Typography, Button } from 'antd';
import { createElement, useState } from 'react';
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from 'react-router-dom';
import { useDirty } from '../../../components/Blocker/dirty';
import HeaderOptions from '../../../components/Header';
import IconAppLogo from "../../../components/IconAppLogo";
import SidebarMenu from '../../../components/Sidebar';
import { ACTION, FEATURE } from "../../../config/roles";
import { useAuth } from "../../../hooks/auth";
import Api from '../../../services/api';
import { openChat } from '../../../utils/chatControl';
import flowieIcon from '../../../assets/flowie.png';
import { Container, HeaderLogoContainer } from './styles';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;
const { confirm } = Modal;

const Authorized = ({ children }) => {
  const { isDirty, setClean } = useDirty();

  const { user, can, } = useAuth();
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(true);
  const [maintenanceMessage, setMaintenanceMessage] = useState(localStorage.getItem('@Auth:MaintenanceMessage'));
  const [pendingTasks, setPendingTasks] = useState(JSON.parse(localStorage.getItem('@Auth:CheckedTasksCount')));
  let navigate = useNavigate();
  
  const hostname = window.location.hostname;
  const isProduction = hostname === 'app.flowy.cc';
  const isQA = hostname.includes('app-qa.flowy.cc');
  const environmentLabel = isQA ? 'QA' : 'DEV';

  const tryNavigate = (path, newTab) => {
    if (isDirty) {
      Modal.confirm({
        content: t('Prompt.Leave'),
        onOk: () => {
          setClean();
          navigate(path);
        },
      });
    } else {
      navigate(path);
    }
  };

  const handleToggle = () => {
    setCollapsed(!collapsed);
  };

  const checkContentLayoutMarginCss = () => {
    if (document.getElementById("site-content-id").style.margin === "0px") {
      document.getElementById("site-content-id").style.margin = '24px 16px';
      document.getElementById("site-content-id").style.padding = '24px';
      document.getElementById("site-content-id").style.background = '#F8F8F8';
    }
  }

  const confirmRead = async () => {
    var config = {
      content: t('ConfirmReadText'),
      okText: t('BtnYes'),
      onOk() {
        localStorage.removeItem('@Auth:MaintenanceMessage', '');
        setMaintenanceMessage('');
        Api.post('/MaintenanceConfirm', null);
      }
    };

    return confirm(config);
  }

  const renderInfoDropdown = (title, rows) => (
    <div
      style={{
        minWidth: 220,
        background: '#fff',
        borderRadius: 8,
        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)',
        padding: 8,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ padding: '8px 12px', fontSize: 14, fontWeight: 500, color: '#262626' }}>
        {title}
      </div>
      <div style={{ height: 1, background: '#f0f0f0', margin: '4px 0' }} />
      {rows.map((row) => (
        <div
          key={row.label}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            padding: '8px 12px',
          }}
        >
          <Text type="secondary">{row.label}</Text>
          <Text>{row.value}</Text>
        </div>
      ))}
    </div>
  );

  const taskInfoRows = pendingTasks != null
    ? [
      { label: t('History.MyTasks'), value: pendingTasks.myTasks },
      ...(can(FEATURE.FORMTASK, [ACTION.PENDINGTASK])
        ? [
          { label: t('History.LocationTasks'), value: pendingTasks.locationTasks },
          { label: t('History.ReportTasks'), value: pendingTasks.reportTasks },
        ]
        : []),
    ]
    : [];

  const documentInfoRows = pendingTasks != null
    ? [{ label: t('Menu.DocumentControl'), value: pendingTasks.documentTasks }]
    : [];

  return (
    <Container>
      <Layout style={{ height: '100vh' }}>
        <Header className="site-layout-background">
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '0 12px',
            height: '64px',
            width: '100%',
            overflow: 'hidden',
            position: 'relative',
            lineHeight: '64px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, height: '64px', lineHeight: '64px' }}>
              <Button
                type="text"
                icon={createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined)}
                onClick={handleToggle}
                style={{
                  fontSize: '18px',
                  color: '#8c8c8c',
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  margin: 0,
                  padding: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#4085c6';
                  e.currentTarget.style.backgroundColor = 'rgba(64, 133, 198, 0.08)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#8c8c8c';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              />
              <Link 
                to="#" 
                onClick={() => { 
                  localStorage.setItem("Flowy.MenuKey", "0"); 
                  tryNavigate(user.isFirstLogin || user.expiredPassword ? "/changePassword" : (user && user.customHomePage ? '/home/custom' : '/home')); 
                }}
                style={{ textDecoration: 'none' }}
              >
                <HeaderLogo />
              </Link>
              {maintenanceMessage != null && maintenanceMessage != "" && maintenanceMessage != 'null' && (
                <div
                  onClick={() => confirmRead()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    backgroundColor: '#fff2e8',
                    border: '1px solid #ffd8bf',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    maxWidth: '50%',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffe7d3';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff2e8';
                  }}
                >
                  <ScheduleOutlined style={{ color: '#fa8c16', fontSize: '16px' }} />
                  <Text style={{ color: '#d46b08', fontSize: '13px', fontWeight: 500 }}>
                    {maintenanceMessage} <span style={{ color: '#fa8c16', textDecoration: 'underline' }}>{t("ConfirmRead")}</span>
                  </Text>
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', height: '64px', lineHeight: '64px' }}>
              {user && (user.isFirstLogin == false && user.expiredPassword == false) && (
                <>
                  <div
                  onClick={() => openChat()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                      padding: '4px 12px',
                    borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      userSelect: 'none',
                    height: '40px',
                    maxHeight: '40px',
                    overflow: 'hidden',
                    lineHeight: '1',
                  }}
                  onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(64, 133, 198, 0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        border: '2px solid transparent',
                        flexShrink: 0,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.borderColor = '#4085c6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  <img
                    src={flowieIcon}
                    alt="FlowIA"
                    style={{
                          width: '24px', 
                          height: '24px', 
                          objectFit: 'contain' 
                        }} 
                      />
                    </div>
                    <div style={{
                      marginLeft: '8px',
                      minWidth: 0,
                      maxWidth: '150px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      <Text
                        strong
                        style={{
                          fontSize: '14px',
                          lineHeight: '1.2',
                          color: '#000',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        FlowIA
                      </Text>
                      <Text
                        type="secondary"
                        style={{
                          fontSize: '12px',
                          lineHeight: '1.2',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {t('ChatBot.Title')}
                      </Text>
                    </div>
                  </div>
                  {((can(FEATURE.DOCUMENTCONTROL, [ACTION.DCCOMMON]) && user && !user.customHomePage && pendingTasks != null && user && (user.isFirstLogin == false && user.expiredPassword == false)) ||
                    ((pendingTasks != null && user && (user.isFirstLogin == false && user.expiredPassword == false)) && !user.customHomePage)) && (
                    <span style={{ margin: '0 4px', color: '#f0f0f0' }}>|</span>
                  )}
                </>
              )}
              
              {can(FEATURE.DOCUMENTCONTROL, [ACTION.DCCOMMON]) && user && !user.customHomePage && (
                <>
                  {(pendingTasks != null && user && (user.isFirstLogin == false && user.expiredPassword == false)) && (
                    <Dropdown
                      trigger={['click']}
                      placement="bottomRight"
                      dropdownRender={() => renderInfoDropdown(t('History.LateDocuments'), documentInfoRows)}
                    >
                      <Button
                        type="text"
                        style={{
                          width: 40,
                          height: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '8px',
                          padding: 0,
                          margin: 0,
                          lineHeight: '1',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(64, 133, 198, 0.08)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <Badge count={pendingTasks.documentTasks} offset={[-2, 2]}>
                          <FolderOutlined style={{ fontSize: 20, color: '#595959' }} />
                        </Badge>
                      </Button>
                    </Dropdown>
                  )}
                  {(pendingTasks != null && user && (user.isFirstLogin == false && user.expiredPassword == false)) && !user.customHomePage && (
                    <span style={{ margin: '0 4px', color: '#f0f0f0' }}>|</span>
                  )}
                </>
              )}
              
              {(pendingTasks != null && user && (user.isFirstLogin == false && user.expiredPassword == false)) && !user.customHomePage && (
                <>
                <Dropdown
                  trigger={['click']}
                  placement="bottomRight"
                  dropdownRender={() => renderInfoDropdown(t('History.LateTasks'), taskInfoRows)}
                >
                  <Button
                    type="text"
                    style={{
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                      padding: 0,
                      margin: 0,
                      lineHeight: '1',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(64, 133, 198, 0.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Badge 
                      count={can(FEATURE.FORMTASK, [ACTION.PENDINGTASK]) ? (pendingTasks.myTasks + pendingTasks.locationTasks + pendingTasks.reportTasks) : pendingTasks.myTasks} 
                      offset={[-2, 2]}
                    >
                      <BellOutlined style={{ fontSize: 20, color: '#595959' }} />
                    </Badge>
                  </Button>
                </Dropdown>
                  <span style={{ margin: '0 4px', color: '#d9d9d9' }}>|</span>
                </>
              )}
              
              <HeaderOptions />
            </div>
          </div>
        </Header>

        <Layout className="site-layout">
          {user && !user.isFirstLogin && !user.expiredPassword &&
            <Sider
              trigger={null}
              collapsible
              collapsed={collapsed}
              onClick={() => checkContentLayoutMarginCss()}
            >
              <SidebarMenu collapsed={collapsed} />
            </Sider>
          }
          <Content id="site-content-id">{children}</Content>
        </Layout>

      </Layout >
    </Container >
  );
};

const HeaderLogo = () => {
  const hostname = window.location.hostname;
  const isProduction = hostname === 'app.flowy.cc';
  const isQA = hostname.includes('app-qa.flowy.cc');
  const environmentLabel = isQA ? 'QA' : 'DEV';
  
  return (
    <HeaderLogoContainer>
      <IconAppLogo />
      {!isProduction && (
        <div style={{
          marginLeft: '8px',
          minWidth: 0,
          maxWidth: '150px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          <Text
            strong
            style={{
              fontSize: '14px',
              lineHeight: '1.2',
              color: '#000',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {environmentLabel}
          </Text>
          <Text
            type="secondary"
            style={{
              fontSize: '12px',
              lineHeight: '1.2',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            environment
          </Text>
        </div>
      )}
    </HeaderLogoContainer >
  )
}

export default Authorized;
